import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Text from '@atlaskit/primitives/text';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { useProductConfigs } from '../../constants/products';
import type { OAuthSource } from '../../utils/events/oauth-events';

import {
	getOAuthSocialProofMessageContent,
	type SupportedOAuthSocialProofMessageProduct,
} from './get-content';
import { messages } from './messages';

const styles = cssMap({
	container: {
		position: 'relative',
		display: 'flex',
		alignItems: 'stretch',
		gap: token('space.075'),
		overflow: 'hidden',
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
		width: '100%',
		height: '160px',
	},
	content: {
		flex: '1 1 auto',
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.250'),
		paddingInlineEnd: token('space.250'),
		minWidth: 0,
		backgroundColor: token('elevation.surface.overlay'),
	},
	productIcon: {
		flex: '0 0 auto',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	titleWrapper: {
		flex: '1 1 auto',
		minWidth: 0,
	},
	titleText: {
		display: 'block',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	imageContainer: {
		flex: '0 0 auto',
		width: '308px',
		overflow: 'hidden',
		position: 'relative',
		display: 'flex',
		alignItems: 'stretch',
		justifyContent: 'stretch',
		backgroundColor: token('color.background.neutral'),
	},
	bannerImage: {
		display: 'block',
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
});

/**
 * Threshold above which we switch from the low-adoption title
 * ("Your team connected {product}") to the high-adoption title
 * ("{percentage}% of your team has connected apps like {product}").
 */
const TEAM_CONNECTED_MID_TIER_MIN = 30;

export const OAUTH_SOCIAL_PROOF_MESSAGE_TEST_ID = 'oauth-social-proof-message';

export interface OAuthSocialProofMessageProps {
	onAuthorize: (source: OAuthSource) => void;
	onDismiss: (e: React.MouseEvent<HTMLElement>) => void;
	oAuthSource: OAuthSource;
	product: SupportedOAuthSocialProofMessageProduct;
	/**
	 * Percentage of the user's team that has already connected this connector. Drives the "social proof" title variant.
	 */
	teamConnectedPercentage?: number;
	/**
	 * When true, hides the preview image and shows a neutral placeholder instead.
	 * Intended for VR snapshots where animated GIFs produce non-deterministic diffs.
	 */
	hidePreviewImage?: boolean;
}

export const OAuthSocialProofMessage = ({
	onAuthorize,
	onDismiss,
	oAuthSource,
	product,
	teamConnectedPercentage = 0,
	hidePreviewImage = false,
}: OAuthSocialProofMessageProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();
	const products = useProductConfigs();
	const { productIcon, description, imageSrc } = getOAuthSocialProofMessageContent(product, {
		colorMode,
	});

	const { displayName } = products[product] || {};

	const productDisplayName: string =
		displayName && typeof displayName === 'string' ? displayName : product;

	// Pick the title variant based on what percentage of the user's team
	// has already connected — a "social proof" nudge.
	//   <30% → "Your team connected {product}"
	//   ≥30% → "{percentage}% of your team has connected apps like {product}"
	const titleMessage =
		teamConnectedPercentage >= TEAM_CONNECTED_MID_TIER_MIN
			? messages.oauthSocialProofMessageTitleHighAdoption
			: messages.oauthSocialProofMessageTitleLowAdoption;

	return (
		<Box xcss={styles.container} testId={OAUTH_SOCIAL_PROOF_MESSAGE_TEST_ID}>
			<Box xcss={styles.content}>
				<Stack space="space.150">
					<Inline space="space.100" alignBlock="center">
						{productIcon ? <Box xcss={styles.productIcon}>{productIcon}</Box> : null}
						<Box xcss={styles.titleWrapper}>
							<Heading size="xsmall">
								<Box as="span" xcss={styles.titleText}>
									<FormattedMessage
										{...titleMessage}
										values={{ percentage: teamConnectedPercentage, product: productDisplayName }}
									/>
								</Box>
							</Heading>
						</Box>
					</Inline>
					<Text size="medium" color="color.text" maxLines={2}>
						<FormattedMessage {...description} />
					</Text>
					<Inline space="space.100" alignBlock="center">
						<Button appearance="primary" onClick={() => onAuthorize(oAuthSource)}>
							<FormattedMessage
								{...messages.oauthSocialProofMessageConnect}
								values={{ product: productDisplayName }}
							/>
						</Button>
						<Button appearance="default" onClick={onDismiss}>
							{formatMessage(messages.dismiss)}
						</Button>
					</Inline>
				</Stack>
			</Box>
			<Box xcss={styles.imageContainer}>
				{!hidePreviewImage && (
					<Box
						as="img"
						src={imageSrc}
						alt={formatMessage(messages.oauthSocialProofMessageImageAlt, {
							product: productDisplayName,
						})}
						xcss={styles.bannerImage}
					/>
				)}
			</Box>
		</Box>
	);
};
